/**
 * 对话里发送的图片和文件的预处理（docs/语音助手.md 4.4，与 iOS AttachmentPrep 一致）：
 * 图片缩到长边 1600 转 JPEG；PDF 提取文字（每页带页码标记，扫描件把前几页渲染成图片）；文本类按 UTF-8 读取（失败时按 GB18030）；其它格式不支持。
 * 文字提取（pdfText / decodeText）也给 read_attachment 工具用（read-attachment.ts）。
 */

export interface PreparedAttachment {
  kind: "image" | "file";
  name: string;
  mime: string;
  size: number;
  /** 图片（或扫描版 PDF 渲染出的页面），data URL */
  imageDataUrls: string[];
  /** 文件里提取出的文字 */
  text: string | null;
  /** 上传为附件的文件（图片是缩小后的 JPEG） */
  uploadFile: File;
  /** 上传后的附件 id */
  assetId?: string;
  /** 重试后仍上传失败 */
  uploadFailed?: boolean;
  /** PDF 的总页数 */
  pageCount?: number;
  /** text 里包含到第几页（PDF 太长时只附前面几页） */
  textPages?: number;
}

export const MAX_TEXT = 60_000;
export const MAX_PDF_PAGES = 30;
export const SCANNED_PAGES = 5;
export const MAX_IMAGE_SIDE = 1600;
export const MAX_IMAGES = 4;
export const MAX_FILES = 5;

/** 文件选择器允许的类型 */
export const ACCEPT = "image/*,application/pdf,.pdf,.txt,.csv,.md,.json,.ics,.html,.htm,text/*";

const TEXT_EXTS = ["txt", "csv", "md", "json", "ics", "html", "htm"];
const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "heic", "bmp"];

/** read_attachment 一次最多返回的文字（工具结果整体不超过 12,000 字，留出 JSON 的余量） */
export const READ_CHARS = 9_000;

export class UnsupportedFile extends Error {
  constructor(name: string) {
    super(`「${name}」暂不支持，请导出为 PDF 或 CSV 再发送`);
    this.name = "UnsupportedFile";
  }
}

function ext(name: string): string {
  return name.split(".").pop()?.toLowerCase() ?? "";
}

export function isImageFile(f: { name: string; type: string }): boolean {
  return f.type.startsWith("image/") || IMAGE_EXTS.includes(ext(f.name));
}

export function isPdfFile(f: { name: string; type: string }): boolean {
  return f.type === "application/pdf" || ext(f.name) === "pdf";
}

export function isTextFile(f: { name: string; type: string }): boolean {
  return f.type.startsWith("text/") || f.type === "application/json" || TEXT_EXTS.includes(ext(f.name));
}

/** 文本解码：先按 UTF-8（严格），失败时按 GB18030 */
export function decodeText(buf: ArrayBuffer | Uint8Array): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder("gb18030").decode(buf);
  }
}

/** 附件的种类：pdf / image / text / file（其它格式读不了内容；与 iOS AttachmentText.kind 一致） */
export function attachmentKind(mime: string, name: string): "pdf" | "image" | "text" | "file" {
  const e = ext(name);
  if (mime === "application/pdf" || e === "pdf") return "pdf";
  if (mime.startsWith("image/") || IMAGE_EXTS.includes(e)) return "image";
  if (mime.startsWith("text/") || mime === "application/json" || TEXT_EXTS.includes(e)) return "text";
  return "file";
}

/** 页码标记，例如"— 第 3 页 —" */
export function pageMarker(n: number): string {
  return `— 第 ${n} 页 —`;
}

export interface PagesText {
  /** 每页前面带页码标记 */
  text: string;
  /** 包含到第几页（一页都没有时是起始页 - 1） */
  lastPage: number;
  /** 去掉页码标记后的字数（判断是不是扫描件） */
  chars: number;
  /** 这一页太长，只截了一部分 */
  cutPage: number | null;
}

/**
 * 按页拼文字（与 iOS AttachmentText.pages 一致）：超出 maxChars 前停下（第一页就超出时截断这一页）。
 * pageText 返回第 n 页（从 1 开始）的文字。
 */
export async function pagesText(from: number, to: number, maxChars: number, pageText: (n: number) => Promise<string | null>): Promise<PagesText> {
  let out = "";
  let count = 0;
  let chars = 0;
  let last = from - 1;
  let cut: number | null = null;
  for (let n = from; n <= to; n++) {
    const body = ((await pageText(n)) ?? "").trim();
    const block = [...`${pageMarker(n)}\n${body}\n\n`];
    if (count + block.length > maxChars) {
      if (last < from) {
        const room = maxChars - count;
        out += block.slice(0, room).join("");
        chars += Math.max(0, Math.min([...body].length, room - pageMarker(n).length - 1));
        last = n;
        cut = n;
      }
      break;
    }
    out += block.join("");
    count += block.length;
    chars += [...body].length;
    last = n;
  }
  return { text: out.trim(), lastPage: last, chars, cutPage: cut };
}

/** 解析页码范围（与 iOS AttachmentText.pageRange 一致）："3-5"、"3–5"、"7"、"6-"（到最后一页）；空 → 全部。超出页数或格式不对返回 null */
export function pageRange(spec: string | null | undefined, pageCount: number): [number, number] | null {
  if (pageCount <= 0) return null;
  const s = String(spec ?? "").replace(/[^0-9\-–—~～至到]/g, "");
  if (!s) return [1, pageCount];
  const parts = s.split(/[-–—~～至到]/);
  if (parts.length > 2) return null;
  const a = /^\d+$/.test(parts[0]!) ? Number(parts[0]) : NaN;
  if (!(a >= 1 && a <= pageCount)) return null;
  if (parts.length === 1) return [a, a];
  const b = parts[1] === "" ? pageCount : /^\d+$/.test(parts[1]!) ? Number(parts[1]) : 0;
  if (b < a) return null;
  return [a, Math.min(b, pageCount)];
}

/** pdf.js 一页的文字 */
export async function pdfPageText(doc: PdfDoc, n: number): Promise<string> {
  const page = await doc.getPage(n);
  const tc = await page.getTextContent();
  let text = "";
  for (const item of tc.items) {
    if ("str" in item) text += item.str + (item.hasEOL ? "\n" : "");
  }
  return text;
}

/** PDF 第 from–to 页的文字，每页前面带页码标记，最多 maxChars 字 */
export function pdfText(doc: PdfDoc, from: number, to: number, maxChars: number): Promise<PagesText> {
  return pagesText(from, to, maxChars, (n) => pdfPageText(doc, n));
}

/** 打开 PDF（文件内容）；打不开返回 null。用完调用 doc.loadingTask.destroy() */
export async function openPdfData(data: ArrayBuffer): Promise<PdfDoc | null> {
  const pdfjs = await loadPdfJs();
  const task = pdfjs.getDocument({ data: new Uint8Array(data) });
  try {
    return await task.promise;
  } catch {
    void task.destroy();
    return null;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error("读取失败"));
    r.readAsDataURL(blob);
  });
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("图片转换失败"))), "image/jpeg", quality);
  });
}

/** 长边缩到 1600 以内（白底，透明 PNG 转 JPEG 不会变黑） */
function drawScaled(source: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const k = Math.min(1, MAX_IMAGE_SIDE / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(w * k));
  canvas.height = Math.max(1, Math.round(h * k));
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export async function prepareImage(file: Blob, name: string): Promise<PreparedAttachment> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new UnsupportedFile(name);
  }
  const canvas = drawScaled(bitmap, bitmap.width, bitmap.height);
  bitmap.close();
  const jpeg = await canvasToJpeg(canvas, 0.8);
  const jpgName = `${name.replace(/\.[^.]+$/, "") || "图片"}.jpg`;
  return {
    kind: "image",
    name: jpgName,
    mime: "image/jpeg",
    size: jpeg.size,
    imageDataUrls: [await blobToDataUrl(jpeg)],
    text: null,
    uploadFile: new File([jpeg], jpgName, { type: "image/jpeg" }),
  };
}

type PdfJs = typeof import("pdfjs-dist");
type PdfDoc = import("pdfjs-dist").PDFDocumentProxy;
let pdfjsPromise: Promise<PdfJs> | null = null;

/** pdf.js 按需加载（不放进首屏包） */
function loadPdfJs(): Promise<PdfJs> {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]).then(([lib, worker]) => {
      lib.GlobalWorkerOptions.workerSrc = (worker as { default: string }).default;
      return lib;
    });
  }
  return pdfjsPromise;
}

async function preparePdf(file: File): Promise<PreparedAttachment> {
  const doc = await openPdfData(await file.arrayBuffer());
  if (!doc) throw new UnsupportedFile(file.name);
  try {
    const pages = await pdfText(doc, 1, Math.max(1, Math.min(doc.numPages, MAX_PDF_PAGES)), MAX_TEXT);
    let text = pages.text;
    const images: string[] = [];
    if (pages.chars < 20) {
      // 扫描件：把前几页渲染成图片给看图模型
      text = "";
      for (let i = 1; i <= Math.min(doc.numPages, SCANNED_PAGES); i++) {
        const page = await doc.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = MAX_IMAGE_SIDE / Math.max(base.width, base.height);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        await page.render({ canvas, viewport, background: "#ffffff" }).promise;
        images.push(await blobToDataUrl(await canvasToJpeg(canvas, 0.75)));
      }
    }
    return {
      kind: "file",
      name: file.name,
      mime: "application/pdf",
      size: file.size,
      imageDataUrls: images,
      text: text || null,
      uploadFile: file,
      pageCount: doc.numPages,
      ...(text ? { textPages: pages.lastPage } : {}),
    };
  } finally {
    void doc.loadingTask.destroy();
  }
}

/** 选择 / 拖进来的文件 → 预处理好的附件；不支持的格式抛 UnsupportedFile */
export async function prepareFile(file: File): Promise<PreparedAttachment> {
  if (isImageFile(file)) return prepareImage(file, file.name);
  if (isPdfFile(file)) return preparePdf(file);
  if (isTextFile(file)) {
    const text = decodeText(await file.arrayBuffer()).slice(0, MAX_TEXT);
    return { kind: "file", name: file.name, mime: file.type || "text/plain", size: file.size, imageDataUrls: [], text, uploadFile: file };
  }
  throw new UnsupportedFile(file.name);
}
